import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { configureAccountStorage } from '@my/platform/account';
import { configureHttp } from '@my/platform/http';
import { App } from '@/App';
import { loadConfig } from '@/configuration/config';
import '@/styles.css';

/**
 * Suite-app identifier for this build, sent as `X-App-Key` and used to
 * namespace per-app localStorage (`my:hub:*`).
 *
 * Unlike the operational apps this is NOT a subscription: every account has
 * My Hub, so the Auth0 Action is given no `subscription` to gate on and the
 * APIs treat `hub` as universal.
 */
const APP_CODE = 'hub';

async function bootstrap() {
  // Load /config.json BEFORE rendering so Auth0Provider, axios, etc. all see
  // a populated config from their first render. If this fails we render a
  // hard error rather than mount a half-broken tree.
  let cfg;
  try {
    cfg = await loadConfig();
  } catch (err) {
    renderConfigError(err);
    return;
  }

  configureAccountStorage(APP_CODE);
  configureHttp({ baseUrl: cfg.apiUrl, appKey: cfg.appKey });

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Missing #root element');

  createRoot(rootEl).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

function renderConfigError(err: unknown) {
  const rootEl = document.getElementById('root');
  if (!rootEl) return;
  const msg = err instanceof Error ? err.message : String(err);
  rootEl.innerHTML = `
    <div style="font-family:system-ui;padding:2rem;color:#991b1b;background:#fef2f2;height:100vh">
      <h1 style="margin-bottom:0.5rem;font-size:1.25rem;font-weight:600">Failed to load /config.json</h1>
      <p>${msg}</p>
    </div>
  `;
}

void bootstrap();
